import { FC, ReactNode, useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { MobileSidebarDrawer } from './MobileSidebarDrawer';
import { useMobile } from '@/hooks/useMobile';
import { BRAND_LOGO_PATH, BRAND_NAME } from '@/config/deutscheBankDemo';

interface LayoutProps {
  children: ReactNode;
  onBack?: () => void;
  mobileBackLabel?: string;
}

export const Layout: FC<LayoutProps> = ({ children, onBack, mobileBackLabel = 'Back' }) => {
  const isMobile = useMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  return (
    <div className="flex h-screen bg-peach">
      <MobileSidebarDrawer
        open={isMobile && mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        showHeader={false}
      >
        <div className="h-full bg-white flex flex-col">
          <Sidebar onBack={onBack} onNavigate={() => setMobileSidebarOpen(false)} />
        </div>
      </MobileSidebarDrawer>

      <aside className="hidden h-full w-[300px] bg-white border-r border-peach-1 md:flex md:flex-col">
        <Sidebar onBack={onBack} />
      </aside>

      <main className="flex-1 flex flex-col h-full min-w-0">
        <div className="md:hidden h-14 px-3 border-b border-peach-1 bg-white flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-peach-1 hover:bg-peach transition-colors"
            aria-label="Open chats"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M4.5 8h7M6.5 12h3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="flex-1 min-w-0 flex items-center justify-center px-1">
            <img
              src={BRAND_LOGO_PATH}
              alt={BRAND_NAME}
              className="h-8 w-auto object-contain"
            />
          </div>

          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-peach-1 text-grey-black hover:bg-peach transition-colors"
              aria-label={mobileBackLabel}
              title={mobileBackLabel}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13 8H3M7 4L3 8l4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <div className="w-9 shrink-0" />
          )}
        </div>

        <div className="flex-1 overflow-auto p-3 md:p-4 relative">{children}</div>
      </main>
    </div>
  );
};
